import { ApplicationConfig, inject, SecurityContext } from '@angular/core';
import { provideRouter, Routes } from '@angular/router';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { canLogin, isAuthenticated, notAuthenticated } from './shared/guard/guard';
import { PageKey } from './shared/services/page/models/page.key';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpEvent, HttpHandlerFn, HttpRequest, provideHttpClient, withInterceptors } from '@angular/common/http';
import { environment } from '../environments/environment';
import { Observable } from 'rxjs';
import { AuthService } from './shared/services/auth/auth.service';
import { jwtDecode } from 'jwt-decode'
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { providePrimeNG } from 'primeng/config';
import { manualAnalysisRoute } from './pages/main/manual-analysis/manual-analysis.config';
import { DialogService } from 'primeng/dynamicdialog'
import { documentsComparisonRoute } from './pages/main/documents-comparison/documents-comparison.config';
import { documentsManageRoute } from './pages/main/documents-manage/documents-manage.config';
import { BYPASS_INTERCEPTORS } from "./shared/services/api/api";
import { usersManageRoute } from './pages/main/users-manage/users-manage.config';
import { informsRoutes } from './pages/main/informs/informs.config';
import { MARKED_OPTIONS, provideMarkdown } from 'ngx-markdown';


export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    canActivate: [canLogin],
    data: {
      title: 'Login',
      fullscreen: true,
      key: PageKey.LOGIN,
    },
  },

  {
    path: 'forgot/password',
    loadComponent: () => import('./pages/forgot-pwd/forgot-pwd.component').then(m => m.ForgotPwdComponent),
    canActivate: [notAuthenticated],
    data: {
      title: 'Esqueci Senha',
      fullscreen: true,
    },
  },

  {
    path: 'reset/password',
    loadComponent: () => import('./pages/new-pwd/new-pwd.component').then(m => m.NewPwdComponent), 
    canActivate: [notAuthenticated],
    data: {
      title: 'Nova Senha', 
      fullscreen: true,
    },
  },

  {
    path: 'main', canActivate: [isAuthenticated],
    children: [
      ...manualAnalysisRoute,
      ...documentsComparisonRoute,
      ...usersManageRoute,
      ...documentsManageRoute,
      ...informsRoutes
    ],
  },
  
  { path: '', redirectTo: 'login', pathMatch: 'full' },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    providePrimeNG({ 
      translation: {
        dayNames: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
        dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
        dayNamesMin: ['Do', 'Se', 'Te', 'Qa', 'Qi', 'Sx', 'Sa'],
        monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
        monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
        today: 'Hoje',
        clear: 'Limpar'
      }
    }),
    provideHttpClient(
      withInterceptors([baseUrlInterceptor, refreshTokenInterceptor, authInterceptor]),
    ),
    provideMarkdown({
      sanitize: SecurityContext.NONE,
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: true,
          breaks: true,
          pedantic: false,
        },
      },
    }),
    provideEnvironmentNgxMask(),
    BrowserAnimationsModule,
    MessageService,
    DialogService,
    ConfirmationService
  ],

};

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  if (req.context.get(BYPASS_INTERCEPTORS)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const token = authService.getToken();

  if (token) {
    const newReq = req.clone({
      headers: req.headers.append('Authorization', 'Bearer ' + token),
    });

    return next(newReq);
  }

  return next(req);
}

export function refreshTokenInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  if (req.context.get(BYPASS_INTERCEPTORS)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const token = authService.getToken();

  if (!token) return next(req);

  const fiveSecondsInMilliseconds = 1_000 * 5;
  const maxSafeExpiresDate = +new Date() + fiveSecondsInMilliseconds;

  if (!isTokenExpired(token, maxSafeExpiresDate)) {
    return next(req);
  }

  authService.logout({ preserveReturnUrl: true }).then();
  return next(req);
}


export function baseUrlInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  if (req.context.get(BYPASS_INTERCEPTORS)) {
    return next(req);
  }

  const newReq = req.clone({
    url: joinApiUrl(environment.apiUrl, req.url),
  });

  return next(newReq);
}

export function joinApiUrl(apiUrl: string, path: string): string {
  if (/^https?:\/\//i.test(path))
    return path;

  const base = apiUrl.replace(/\/+$/, '');
  const relativePath = path.replace(/^\/+/, '');

  return `${base}/${relativePath}`;
}

export function isTokenExpired(token: string, maxExpiresDate: number): boolean {
  const jwtPayload: { exp: number } = jwtDecode(token);
  return maxExpiresDate >= +new Date(jwtPayload.exp * 1000);
}
